import { useEffect, useRef } from "react";
import type { Attachment, ChatMessage } from "@/api/types";
import BotBubble from "./BotBubble";
import { Paperclip, Pencil } from "lucide-react";

/** bytes → '1.2MB' / '340KB'. 알 수 없으면 빈 문자열. */
function fmtSize(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n) || n < 0) return "";
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)}KB`;
  return `${(n / (1024 * 1024)).toFixed(1)}MB`;
}

/** 질문 말풍선 아래 첨부 파일 칩 — 요청 문구와 보낸 파일을 함께 보여준다. */
function AttachmentChips({ items }: { items: Attachment[] }) {
  return (
    <div className="mt-1 flex max-w-[85%] flex-wrap justify-end gap-1">
      {items.map((a, i) => {
        const size = fmtSize(a.size);
        return (
          <span
            key={`${i}-${a.name}`}
            title={size ? `${a.name} (${size})` : a.name}
            className="flex max-w-full items-center gap-1 rounded-lg border border-border bg-card px-2 py-1 text-xs text-muted-foreground"
          >
            <Paperclip className="h-3 w-3 shrink-0" />
            <span className="truncate">{a.name}</span>
            {size && <span className="shrink-0 opacity-70">{size}</span>}
          </span>
        );
      })}
    </div>
  );
}

interface Props {
  messages: ChatMessage[];
  customer?: string;
  onConfirm: (botId: string, token: string, accept: boolean) => void;
  onFeedback: (id: string, fb: 1 | -1) => void;
  onEdit?: (text: string) => void;
  onRegenerate?: (question: string) => void;
  onRegenerateSources?: (question: string, excludeUrls: string[], customer?: string) => void;
  onSuggest?: (question: string) => void;
  onBookmark?: (id: string, bookmarkId: number | null) => void;
}

export default function MessageList({
  messages,
  customer,
  onConfirm,
  onFeedback,
  onEdit,
  onRegenerate,
  onRegenerateSources,
  onSuggest,
  onBookmark,
}: Props) {
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-4">
      {messages.map((m, i) => {
        const prevQuestion =
          i > 0 && messages[i - 1].role === "user" ? messages[i - 1].text : "";
        return m.role === "user" ? (
          <div key={m.id} className="group flex flex-col items-end">
            <div className="flex items-center gap-1">
              {onEdit && (
                <button
                  onClick={() => onEdit(m.text)}
                  className="shrink-0 rounded-md p-1 text-muted-foreground opacity-0 hover:bg-muted group-hover:opacity-100"
                  aria-label="질문 편집"
                  title="편집해서 다시 질문"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
              <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-[#0E1E2D] px-3.5 py-2 text-sm text-white">
                {m.text}
              </div>
            </div>
            {(m.attachments?.length ?? 0) > 0 && (
              <AttachmentChips items={m.attachments!} />
            )}
          </div>
        ) : (
          <BotBubble
            key={m.id}
            m={m}
            prevQuestion={prevQuestion}
            customer={customer}
            onConfirm={onConfirm}
            onFeedback={onFeedback}
            onRegenerate={onRegenerate}
            onRegenerateSources={onRegenerateSources}
            onSuggest={onSuggest}
            onBookmark={onBookmark}
          />
        );
      })}
      <div ref={endRef} />
    </div>
  );
}
